<template>
  <v-dialog
    :value="value"
    max-width="720"
    persistent
    scrollable
    data-testid="accruals-sync-upload-modal"
    @input="$emit('input', $event)"
  >
    <v-card class="accruals-sync-upload-modal">
      <v-card-title class="accruals-sync-upload-modal__header white--text">
        <span class="accruals-sync-upload-modal__title">Sync {{ entityLabel }}</span>
        <v-spacer />
        <v-btn
          icon
          dark
          small
          class="accruals-sync-upload-modal__close-btn"
          data-testid="accruals-sync-upload-modal__close-btn"
          :disabled="busy"
          @click="close"
        >
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="accruals-sync-upload-modal__body">
        <section
          class="accruals-sync-upload-modal__status"
          data-testid="accruals-sync-upload-modal__status"
        >
          <div class="accruals-sync-upload-modal__status-header">
            <h5 class="accruals-sync-upload-modal__step-title">Sync status</h5>
            <span class="accruals-sync-upload-modal__status-hint">Refreshes every 20s</span>
          </div>

          <div
            v-if="bootstrapLoading"
            class="accruals-sync-upload-modal__status-loading"
            data-testid="accruals-sync-upload-modal__status-loading"
          >
            <v-progress-circular indeterminate color="primary" size="20" width="2" />
            <span>Loading sync status…</span>
          </div>

          <div v-else class="accruals-sync-upload-modal__status-grid">
            <div class="accruals-sync-upload-modal__status-row">
              <span class="accruals-sync-upload-modal__status-label">SilkRoute flow</span>
              <v-chip
                small
                label
                :color="flowStatusColor"
                text-color="white"
                data-testid="accruals-sync-upload-modal__flow-status"
              >
                {{ flowStatusLabel }}
              </v-chip>
            </div>

            <div v-if="uploaded" class="accruals-sync-upload-modal__status-row">
              <span class="accruals-sync-upload-modal__status-label">Source file</span>
              <v-chip
                small
                label
                :color="uploadStatusChipColor"
                text-color="white"
                data-testid="accruals-sync-upload-modal__upload-status"
              >
                {{ uploadStatusLabel }}
              </v-chip>
            </div>

            <div
              v-if="showColumnMappingStatus"
              class="accruals-sync-upload-modal__status-row"
            >
              <span class="accruals-sync-upload-modal__status-label">Column mapping</span>
              <v-chip
                small
                label
                :color="columnMappingChipColor"
                text-color="white"
                data-testid="accruals-sync-upload-modal__mapping-status"
              >
                {{ columnMappingLabel }}
              </v-chip>
            </div>

            <div
              v-if="taskProgressLabel"
              class="accruals-sync-upload-modal__status-row accruals-sync-upload-modal__status-row--full"
            >
              <span class="accruals-sync-upload-modal__status-label">Progress</span>
              <span class="accruals-sync-upload-modal__status-value">{{ taskProgressLabel }}</span>
            </div>
          </div>
        </section>

        <section
          class="accruals-sync-upload-modal__step accruals-sync-upload-modal__step--upload"
          data-testid="accruals-sync-upload-modal__step-upload"
        >
          <div class="accruals-sync-upload-modal__step-header">
            <div>
              <h5 class="accruals-sync-upload-modal__step-title">
                {{ uploaded ? 'Source file landed' : 'Upload source file' }}
              </h5>
              <p class="accruals-sync-upload-modal__step-desc">
                <template v-if="!uploaded">
                  Upload CSV or Excel for <strong>{{ entityLabel }}</strong>.
                  The file lands in client S3 and is chunked automatically.
                </template>
                <template v-else-if="uploadStatus === 'header_mismatch'">
                  File headers do not match the saved column mapping. Update the mapping on this
                  page and re-upload.
                </template>
                <template v-else-if="uploadStatus === 'chunked' && !columnMappingSaved">
                  File is chunked. Column mapping is the next step and is not saved yet.
                  Map the columns on this page and save them, then use <strong>Ingest now</strong>.
                </template>
                <template v-else-if="uploadStatus === 'chunked' && columnMappingSaved">
                  File is chunked. Column mapping is saved. Use <strong>Ingest now</strong>
                  to load it into the client database.
                </template>
                <template v-else>
                  File is in S3. Chunking runs next. Column mapping is checked after the file is chunked.
                </template>
              </p>
            </div>
            <v-btn
              v-if="uploaded"
              color="white"
              class="accruals-sync-upload-modal__reupload-btn secondaryBtn d-flex align-center"
              data-testid="accruals-sync-upload-modal__reupload-btn"
              :disabled="busy"
              @click="reupload"
            >
              <v-icon class="pr-1">mdi-reload</v-icon>
              <span>Re-Upload</span>
            </v-btn>
          </div>

          <div
            v-if="uploading"
            class="accruals-sync-upload-modal__validating"
            data-testid="accruals-sync-upload-modal__validating"
          >
            <v-progress-circular indeterminate color="primary" size="32" width="3" />
            <p class="accruals-sync-upload-modal__validating-text">Uploading to S3…</p>
          </div>

          <div
            v-else-if="!uploaded"
            class="accruals-sync-upload-modal__dropzone"
            data-testid="accruals-sync-upload-modal__dropzone"
          >
            <DragDropfile
              ref="dragDrop"
              :files="files"
              :multiplefiles="false"
              accept=".xlsx,.xls,.csv"
              :reset-trigger="resetTrigger"
              data-testid="accruals-sync-upload-modal__drag-drop"
              @update:files="onFilesUpdate"
            />
          </div>

          <div
            v-else
            class="accruals-sync-upload-modal__landed"
            data-testid="accruals-sync-upload-modal__landed"
          >
            <v-chip small label :color="uploadStatusChipColor" text-color="white" class="mr-2">
              {{ uploadStatusLabel }}
            </v-chip>
            <span class="accruals-sync-upload-modal__landed-file">{{ landedFileName }}</span>
          </div>
        </section>
      </v-card-text>

      <v-card-actions class="accruals-sync-upload-modal__footer">
        <v-spacer />
        <v-btn
          class="accruals-sync-upload-modal__cancel-btn secondaryBtn"
          data-testid="accruals-sync-upload-modal__cancel-btn"
          :disabled="busy"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          v-if="!uploaded"
          class="accruals-sync-upload-modal__primary-btn primaryBtn ml-0"
          :loading="uploading"
          :disabled="!selectedFile || uploading"
          data-testid="accruals-sync-upload-modal__upload-btn"
          @click="uploadToS3"
        >
          Upload
        </v-btn>
        <v-btn
          v-else
          class="accruals-sync-upload-modal__primary-btn primaryBtn ml-0"
          data-testid="accruals-sync-upload-modal__done-btn"
          :disabled="busy"
          @click="close"
        >
          Done
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DragDropfile from '@/views/Inputs/DragDropfile.vue';
import { getSyncEntityLabel } from '../accrualsDataConstants';

const STATUS_POLL_MS = 20000;

export default {
  name: 'AccrualsSyncUploadModal',
  components: { DragDropfile },
  props: {
    value: { type: Boolean, default: false },
    entityType: { type: String, default: 'open_po' },
    projectId: { type: String, default: '' },
    cid: { type: String, default: '' },
  },
  data() {
    return {
      files: [],
      resetTrigger: false,
      uploading: false,
      uploaded: false,
      uploadStatus: '',
      uploadedAt: '',
      columnMappingSaved: false,
      landedFileName: '',
      bootstrapLoading: false,
      statusPollTimer: null,
      clientFlowStatus: null,
    };
  },
  computed: {
    entityLabel() {
      return getSyncEntityLabel(this.entityType);
    },
    selectedFile() {
      const file = Array.isArray(this.files) ? this.files[0] : this.files;
      return file || null;
    },
    busy() {
      return this.uploading;
    },
    uploadStatusLabel() {
      if (this.uploadStatus === 'chunked') return 'Chunked';
      if (this.uploadStatus === 'header_mismatch') return 'Header mismatch';
      if (this.uploadStatus === 'landed') return 'Landed';
      return this.uploadStatus || 'Uploaded';
    },
    showColumnMappingStatus() {
      return this.uploadStatus === 'chunked' || this.uploadStatus === 'header_mismatch';
    },
    columnMappingLabel() {
      return this.columnMappingSaved ? 'Mapped' : 'Awaiting column mapping';
    },
    columnMappingChipColor() {
      return this.columnMappingSaved ? 'green' : 'orange';
    },
    accruals() {
      return this.$store.state.m_financialclose?.accruals || {};
    },
    flowStatusSource() {
      if (this.projectId) return this.accruals.flowStatus;
      return this.clientFlowStatus;
    },
    entityFlowStatus() {
      return this.flowStatusSource?.entities?.[this.entityType] || {};
    },
    flowStatusLabel() {
      const status = this.entityFlowStatus.status || 'not_started';
      const rejected = Number(this.entityFlowStatus.ingest_rejects?.rejected) || 0;
      if (status === 'completed' && rejected > 0) return 'Completed with rejected rows';
      const labels = {
        not_started: 'Not started',
        in_progress: 'In progress',
        'in-progress': 'In progress',
        completed: 'Completed',
        failed: 'Failed',
        cancelled: 'Cancelled',
      };
      return labels[status] || status;
    },
    flowStatusColor() {
      const status = this.entityFlowStatus.status;
      const rejected = Number(this.entityFlowStatus.ingest_rejects?.rejected) || 0;
      if (status === 'completed' && rejected > 0) return 'orange';
      if (status === 'completed') return 'green';
      if (status === 'failed' || status === 'cancelled') return 'red';
      if (status === 'in_progress' || status === 'in-progress') return 'blue';
      return 'grey';
    },
    uploadStatusChipColor() {
      if (this.uploadStatus === 'chunked') return 'green';
      if (this.uploadStatus === 'header_mismatch') return 'red';
      return 'orange';
    },
    taskProgressLabel() {
      const summary = this.entityFlowStatus.task_summary;
      if (!summary || typeof summary !== 'object') return '';
      const total = Number(summary.total);
      const completed = Number(summary.completed);
      if (!total) return '';
      return `${completed}/${total} tasks completed`;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.bootstrapModal();
        this.startStatusPolling();
      } else {
        this.stopStatusPolling();
        this.resetLocalState();
      }
    },
  },
  beforeDestroy() {
    this.stopStatusPolling();
  },
  methods: {
    close() {
      if (this.busy) return;
      this.stopStatusPolling();
      this.resetLocalState();
      this.$emit('input', false);
    },
    resetLocalState() {
      this.files = [];
      this.uploading = false;
      this.uploaded = false;
      this.uploadStatus = '';
      this.uploadedAt = '';
      this.columnMappingSaved = false;
      this.landedFileName = '';
      this.bootstrapLoading = false;
      this.clientFlowStatus = null;
      this.resetDragDrop();
    },
    startStatusPolling() {
      this.stopStatusPolling();
      this.statusPollTimer = setInterval(() => {
        this.refreshSyncStatus();
      }, STATUS_POLL_MS);
    },
    stopStatusPolling() {
      if (this.statusPollTimer) {
        clearInterval(this.statusPollTimer);
        this.statusPollTimer = null;
      }
    },
    async bootstrapModal() {
      this.resetLocalState();
      this.bootstrapLoading = true;
      try {
        await Promise.all([this.loadFlowStatus(), this.loadExistingUploadState()]);
      } finally {
        this.bootstrapLoading = false;
      }
    },
    async loadFlowStatus() {
      if (this.projectId) {
        await this.$store.dispatch('accruals/loadFlowStatus', {
          projectId: this.projectId,
          cid: this.cid,
        });
        return;
      }
      try {
        this.clientFlowStatus = await this.$store.dispatch('accruals/loadMappingFlowStatus', {
          cid: this.cid,
        });
      } catch (err) {
        console.log('[AccrualsSyncUploadModal]: loadFlowStatus', err);
      }
    },
    async loadExistingUploadState() {
      let headers = null;
      try {
        headers = await this.$store.dispatch('accruals/loadSourceHeaders', {
          projectId: this.projectId,
          cid: this.cid,
          entityType: this.entityType,
        });
      } catch (err) {
        return;
      }

      const hasHeaders = Array.isArray(headers?.source_headers) && headers.source_headers.length > 0;
      if (!hasHeaders && !headers?.upload_status) return;

      this.uploaded = true;
      this.uploadStatus = headers.upload_status || 'landed';
      this.uploadedAt = headers.uploaded_at || '';
      this.landedFileName = this.fileNameFromHeaders(headers);
      await this.loadMappingFlag();
    },
    async loadMappingFlag() {
      try {
        const normalization = await this.$store.dispatch('accruals/loadNormalizationConfig', {
          projectId: this.projectId,
          cid: this.cid,
          entityType: this.entityType,
        });
        this.columnMappingSaved = !!(normalization?.configured && normalization?.mapping_complete);
      } catch (err) {
        this.columnMappingSaved = false;
      }
    },
    fileNameFromHeaders(headers) {
      const key = headers?.s3?.key || headers?.s3?.Key || '';
      if (key) {
        const parts = String(key).split('/');
        return parts[parts.length - 1] || 'Uploaded file';
      }
      return 'Uploaded file';
    },
    async refreshSyncStatus() {
      if (!this.value) return;
      try {
        await this.loadFlowStatus();
        if (this.uploaded) {
          const headers = await this.$store.dispatch('accruals/loadSourceHeaders', {
            projectId: this.projectId,
            cid: this.cid,
            entityType: this.entityType,
          });
          if (headers?.upload_status) {
            this.uploadStatus = headers.upload_status;
          }
          if (headers?.uploaded_at) {
            this.uploadedAt = headers.uploaded_at;
          }
          await this.loadMappingFlag();
        }
      } catch (err) {
        // Keep last known status visible during polling.
      }
    },
    resetDragDrop() {
      this.resetTrigger = false;
      this.$nextTick(() => {
        this.resetTrigger = true;
      });
    },
    onFilesUpdate(files) {
      this.files = files || [];
    },
    reupload() {
      this.uploaded = false;
      this.uploadStatus = '';
      this.uploadedAt = '';
      this.landedFileName = '';
      this.files = [];
      this.resetDragDrop();
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.$refs.dragDrop?.$refs?.file?.click();
        });
      });
    },
    async uploadToS3() {
      if (!this.selectedFile) return;
      this.uploading = true;
      try {
        const data = await this.$store.dispatch('accruals/uploadSource', {
          projectId: this.projectId,
          cid: this.cid,
          entityType: this.entityType,
          file: this.selectedFile,
        });
        this.uploaded = true;
        this.landedFileName = this.selectedFile.name || data?.s3?.key || 'Uploaded file';
        this.uploadStatus = data?.status || 'landed';
        this.uploadedAt = new Date().toISOString();
        await this.refreshSyncStatus();
      } catch (err) {
        // Store surfaces snackbar.
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsSyncUploadModal.scss';
</style>
